import { useState } from "react"

import { recipeFromNotebook } from "../utils/reportRecipe"
import type {
  Cadence,
  Notebook,
  ReportConfig,
  Schedule,
} from "../utils/reportTypes"

/** A saved notebook to repeat, with the recipe its schedule should run. */
interface ScheduleRequest {
  template: Notebook
  cadence: Cadence
  recipe: ReportConfig
}

interface SavedSchedule {
  schedule: Schedule
  message: string
}

/**
 * Schedules saved notebooks and keeps a failed attempt retryable until it
 * saves or the user keeps the notebook unscheduled. A retry schedules the
 * exact revision and recipe of the failed attempt, not later edits, renames or
 * Sofia revisions of the notebook.
 */
export function useNotebookSchedule({
  run,
  save,
  onScheduled,
}: {
  run: (
    label: string,
    operation: (signal: AbortSignal) => Promise<void>,
  ) => Promise<void>
  save: (
    request: ScheduleRequest,
    signal: AbortSignal,
  ) => Promise<SavedSchedule>
  onScheduled: (saved: SavedSchedule) => void
}) {
  const [failed, setFailed] = useState<ScheduleRequest | null>(null)
  const schedule = (request: ScheduleRequest) =>
    void run("Saving schedule", async (signal) => {
      let saved: SavedSchedule
      try {
        saved = await save(request, signal)
      } catch (failure: unknown) {
        // Any failed attempt, from the retry or Schedule this notebook, stays retryable.
        if (!signal.aborted) {
          setFailed(request)
        }
        throw failure
      }
      setFailed(null)
      onScheduled(saved)
    })
  return {
    fail: setFailed,
    /** Repeats a saved notebook with its own recipe, without generating another. */
    scheduleSaved: (notebook: Notebook, cadence: Cadence) =>
      schedule({
        template: notebook,
        cadence,
        recipe: recipeFromNotebook(notebook),
      }),
    /** The retry banner's state while the open notebook is the one left unscheduled. */
    bannerFor: (notebookId: string | undefined) =>
      failed && failed.template.id === notebookId
        ? {
            cadence: failed.cadence,
            onRetry: () => schedule(failed),
            onDiscard: () => setFailed(null),
          }
        : null,
  }
}
